import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSwipeable } from 'react-swipeable'

// Components
import CarouselTextSplitItem from '../CarouselTextSplitItem'
import CarouselNav from '../CarouselNav'
import Container from '../Container'
import Grid from '../Grid'
import GridItem from '../GridItem'
import RichText from '../RichText'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'

// Styles
import {
  CarouselTextSplitWrapper,
  CarouselTextSplitMain,
  CarouselTextSplitText,
  CarouselTextSplitContainer,
  CarouselTextSplitHeading,
  CarouselTextSplitTrack,
} from './index.style'
import { breakpoints } from '../../styles/vars/breakpoints.style'

const CarouselTextSplit = ({
  id,
  featured,
  text,
  carouselItems,
  linkId,
  nextSlideLabel,
  prevSlideLabel,
  background,
  color,
}) => {
  const [currentIndex, setCurrentIndex] = useState(carouselItems.length)
  const [repeatingItems, setRepeatingItems] = useState(null)
  const [transitions, setTransitions] = useState(true)

  const items = useRef([])
  const itemCount = useRef(carouselItems.length)
  const repeatingItemCount = useRef(null)

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (window.innerWidth < breakpoints.tabletL) {
        nextSlide()
      }
    },
    onSwipedRight: () => {
      if (window.innerWidth < breakpoints.tabletL) {
        prevSlide()
      }
    },
  })

  const transitionTimeout = useRef(null)

  const appendArrayToArray = (arr1, arr2) => {
    for (let x = 0; x < arr1.length; x++) {
      arr2.push(arr1[x])
    }
  }

  useEffect(() => {
    items.current = []
    appendArrayToArray(carouselItems, items.current)
    appendArrayToArray(carouselItems, items.current)
    appendArrayToArray(carouselItems, items.current)

    repeatingItemCount.current = items.current.length

    setRepeatingItems(items.current)
  }, [carouselItems])

  const prevSlide = useCallback(() => {
    if (transitions) return
    setTransitions(true)
    setCurrentIndex(currentIndex => currentIndex - 1)
  }, [transitions])

  const nextSlide = useCallback(() => {
    if (transitions) return
    setTransitions(true)
    setCurrentIndex(currentIndex => currentIndex + 1)
  }, [transitions])

  const resetSlide = useCallback(() => {
    setCurrentIndex(carouselItems.length)
  }, [carouselItems])

  const enableSlide = useCallback(() => {
    transitionTimeout.current = null
    setTransitions(false)
    if (currentIndex === 0 || currentIndex === itemCount.current * 2)
      resetSlide()
  }, [currentIndex, resetSlide])

  useEffect(() => {
    transitionTimeout.current = setTimeout(enableSlide, 1200)

    return () => {
      if (transitionTimeout.current) clearTimeout(transitionTimeout.current)
    }
  }, [currentIndex, enableSlide])

  return (
    <>
      <ScrollSection>
        <CarouselTextSplitWrapper
          id={linkId && linkId}
          bgColor={background}
          color={color}
        >
          <Spacer size={[60, 115]} />
          <CarouselTextSplitMain {...handlers}>
            <Container>
              <Grid>
                <GridItem desk={4} deskStart={2} deskAlign={`flex-end`}>
                  <CarouselTextSplitText>
                    <CarouselTextSplitHeading>
                      <RichText id={`${id}-text`} content={text} />
                    </CarouselTextSplitHeading>
                  </CarouselTextSplitText>
                </GridItem>
                <GridItem desk={6} deskStart={7} deskAlign={`flex-end`}>
                  <CarouselTextSplitContainer featured={featured}>
                    <CarouselTextSplitTrack featured={featured}>
                      {repeatingItems &&
                        React.Children.toArray(
                          repeatingItems.map((item, itemIndex) => {
                            return (
                              <CarouselTextSplitItem
                                {...item}
                                featured={true}
                                id={id + '-item-' + itemIndex}
                                index={itemIndex - currentIndex}
                                transitions={transitions}
                                color={color}
                              />
                            )
                          })
                        )}
                    </CarouselTextSplitTrack>
                    <CarouselNav
                      id={`${id}-nav-featured`}
                      align={`flex-end`}
                      delay={0.8}
                      color={color}
                      prevEvent={prevSlide}
                      nextEvent={nextSlide}
                    />
                  </CarouselTextSplitContainer>
                </GridItem>
              </Grid>
            </Container>
          </CarouselTextSplitMain>
          <Spacer size={[100, featured ? 100 : 240]} />
        </CarouselTextSplitWrapper>
      </ScrollSection>
    </>
  )
}

CarouselTextSplit.propTypes = {
  id: PropTypes.string.isRequired,
  text: PropTypes.object,
  carouselItems: PropTypes.array,
  background: PropTypes.string,
  color: PropTypes.string,
}

export default CarouselTextSplit
